import classNames from "classnames";
import { DateTime } from "luxon";
import { useContext, useMemo } from "react";
import { GroupMatch } from "./components/GroupMatch";
import { GroupResult } from "./components/GroupResult";
import { getEliminatedTeams } from "./helpers/getEliminatedTeams";
import { getQualifyingTeams } from "./helpers/getQualifyingTeams";
import { SwissTournamentContext } from "../../SwissBracketComponent";
import { Bracket, BracketGroup } from "@portal-frontend-ssr/blast-api";
import { ClientOnly } from "remix-utils/client-only";

interface SwissGroupProps {
  group: BracketGroup;
  playoffs?: Bracket;
}

const SwissGroup = ({ group }: SwissGroupProps) => {
  const formattedDate = DateTime.fromISO(group.date).toFormat("dd/L");
  const swissContext = useContext(SwissTournamentContext);

  const isHovering = useMemo(() => {
    return !group.matches.some((match) => {
      if (!swissContext?.hoveredTeamUuid) {
        return true;
      }
      if (match.teamA?.uuid === swissContext?.hoveredTeamUuid) {
        return true;
      }
      if (match.teamB?.uuid === swissContext?.hoveredTeamUuid) {
        return true;
      }
      return false;
    });
  }, [group.matches, swissContext?.hoveredTeamUuid]);

  return (
    <div
      className={classNames("my-2 flex flex-col gap-0.5 rounded-small bg-background-90 p-1 transition-all", {
        "opacity-50": isHovering,
      })}
    >
      <div className="flex gap-2">
        {group.numberQualifiedTeams &&
          group.numberQualifiedTeams === group.matches.length &&
          group.numberQualifiedTeams > 0 && (
            <GroupResult
              numberOfTeams={group.numberQualifiedTeams}
              title="Advancing"
              teams={getQualifyingTeams(group)}
              state="success"
            />
          )}
        {group.numberQualifiedTeams && group.matches.length !== group.numberQualifiedTeams ? (
          <GroupResult
            numberOfTeams={group.matches.length}
            title="playoffs"
            teams={getQualifyingTeams(group)}
            state="playoffs"
          />
        ) : null}
      </div>
      <div className="flex w-full items-center justify-between bg-background-90 p-1 px-2 first:pl-3 first:text-left last:pr-3 last:text-right">
        <ClientOnly fallback={<div className="h-2 w-6 rounded-full bg-background-80" />}>
          {() => (
            <time
              className="block text-neutral font-style-label-l4"
              dateTime={DateTime.fromISO(group.date).toISODate()!}
              title={DateTime.fromISO(group.date).toLocaleString(DateTime.DATE_FULL)}
            >
              {formattedDate}
            </time>
          )}
        </ClientOnly>
        <span className="tracking-widest font-style-heading-h4">{group.id}</span>
        <span className="text-neutral font-style-label-l4">{group.format}</span>
      </div>
      {group.matches.map((match) => (
        <GroupMatch match={match} key={match.uuid} otherMatches={group.matches} />
      ))}
      {group.numberEliminatedTeams && group.numberEliminatedTeams > 0 && (
        <GroupResult
          numberOfTeams={group.numberEliminatedTeams}
          title={group.hasLastChance ? "Last Chance" : "Eliminated"}
          teams={getEliminatedTeams(group)}
          state="fail"
        />
      )}
    </div>
  );
};

export { SwissGroup };
